import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface FilePreviewViewerProps {
  file: File;
  className?: string;
}

const FilePreviewViewer: React.FC<FilePreviewViewerProps> = ({ file, className = '' }) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [objectUrl, setObjectUrl] = useState('');

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const isImage = file.type.startsWith('image/');
  const isPdf = file.type === 'application/pdf';

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleFit = () => setZoom(1);

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Preview area */}
      <div className="relative flex-1 overflow-auto rounded-lg border border-border bg-muted/30 min-h-[300px]">
        {isPdf && (
          <object
            data={`${objectUrl}#toolbar=0`}
            type="application/pdf"
            className="h-full w-full min-h-[300px]"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
          >
            <p className="p-4 text-sm text-muted-foreground">{t('PDF preview not available')}</p>
          </object>
        )}
        {isImage && (
          <div className="flex items-center justify-center p-2 overflow-auto h-full">
            <img
              src={objectUrl}
              alt={file.name}
              className="max-w-full transition-transform"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
            />
          </div>
        )}
        {!isPdf && !isImage && (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <svg className="h-16 w-16 text-muted-foreground/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            <p className="mt-2 text-sm text-muted-foreground">{t('Preview not available for this file type')}</p>
          </div>
        )}
      </div>

      {/* Zoom controls */}
      <div className="mt-2 flex items-center justify-center gap-1">
        <button
          type="button"
          onClick={handleZoomOut}
          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label={t('Zoom out') as string}
          title={t('Zoom out') as string}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM13.5 10.5h-6" />
          </svg>
        </button>
        <button
          type="button"
          onClick={handleFit}
          className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          onClick={handleZoomIn}
          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          aria-label={t('Zoom in') as string}
          title={t('Zoom in') as string}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default FilePreviewViewer;
